// Status as a shape, not just a word.
//
// Ported from Zanetto Tools (phase 7 stage 2), with Dayboard's own vocabulary —
// its job and resource statuses are not the same set. Quotes arrived in round
// 43 with their own set below; ZT's variation kind still has no equivalent,
// because a Dayboard job carries no money.
//
// Colour is a second channel, never the only one: the label always says what
// the status is, so this stays readable to anyone who cannot separate the hues.

export type BadgeTone = 'neutral' | 'info' | 'good' | 'warn' | 'bad' | 'done' | 'active';

/**
 * A soft -50 fill with an inset ring of the same hue, rather than a flat -100
 * block. The ring is what gives the chip an edge: a flat tint with no boundary
 * reads as a smudge behind the words, and on the gray-50 page background a
 * -100 fill is barely a shade away from the surface it sits on. Text stays at
 * -700/-800, which clears AA on the tint.
 *
 * `info` STAYS BLUE after the 2026-08-31 rebrand, and that is a decision, not
 * a line that was missed. These six tones are a semantic set, and the set is
 * only useful while each hue means exactly one thing. Orange is now the brand,
 * so an orange `info` chip would say "Dayboard" rather than "here is a fact
 * about this row", and it would sit one hue away from `warn` — the pair a
 * glance most needs to tell apart. Blue became AVAILABLE for information
 * precisely because the brand stopped using it. Same call, same reasoning, as
 * `.banner-info` in design-system.css.
 */
const TONE: Record<BadgeTone, string> = {
  neutral: 'bg-gray-50 text-gray-700 ring-gray-500/25',
  info: 'bg-blue-50 text-blue-700 ring-blue-600/25',
  good: 'bg-green-50 text-green-800 ring-green-600/25',
  warn: 'bg-amber-50 text-amber-800 ring-amber-600/30',
  bad: 'bg-red-50 text-red-700 ring-red-600/25',
  done: 'bg-purple-50 text-purple-700 ring-purple-600/25',
  // Movement, without joining the green/amber/red set that already means
  // done, careful and stopped. Round 34.
  active: 'bg-teal-50 text-teal-800 ring-teal-600/25',
};

type Entry = [BadgeTone, string];

// Round 34. `scheduled` and `in_progress` were both `info` — the same blue
// chip for the two statuses a manager most needs to tell apart when scanning
// the list. Scheduled keeps blue, which is what blue means here (a fact about
// the calendar, derived by the server); in progress takes the new `active`
// teal. `invoiced` takes `done`, the purple tone that was defined and unused.
const JOB: Record<string, Entry> = {
  pending: ['neutral', 'Pending'],
  scheduled: ['info', 'Scheduled'],
  in_progress: ['active', 'In progress'],
  on_hold: ['warn', 'On hold'],
  completed: ['good', 'Completed'],
  invoiced: ['done', 'Invoiced'],
  cancelled: ['bad', 'Cancelled'],
};

/**
 * Quote statuses (round 43). The semantic set, not ZT's palette: sent is
 * blue because it is a fact (it is out with the client), won is green, lost
 * is red, on hold is amber (careful, it is parked), and expired is grey
 * rather than ZT's orange, because orange is the brand.
 */
const QUOTE: Record<string, Entry> = {
  draft: ['neutral', 'Draft'],
  sent: ['info', 'Sent'],
  on_hold: ['warn', 'On hold'],
  won: ['good', 'Won'],
  lost: ['bad', 'Lost'],
  expired: ['neutral', 'Expired'],
};

const RESOURCE: Record<string, Entry> = {
  available: ['good', 'Available'],
  unavailable: ['warn', 'Unavailable'],
  archived: ['neutral', 'Archived'],
};

/**
 * What a resource is doing *today*, derived from the live schedule rather than
 * stored on the row. Zanetto Tools calls this "Status Today" and it is the
 * single most useful column in its resource list — it answers "where is this
 * thing right now" instead of "what did someone last set it to".
 */
const TODAY: Record<string, Entry> = {
  available: ['good', 'Free today'],
  in_use: ['info', 'On a job'],
  away: ['warn', 'Away'],
  unavailable: ['warn', 'Unavailable'],
  archived: ['neutral', 'Archived'],
};

const USER: Record<string, Entry> = {
  active: ['good', 'Active'],
  invited: ['info', 'Invited'],
  deactivated: ['neutral', 'Deactivated'],
};

/**
 * Invoice states. "Payment failed" is `bad` rather than `warn` because it is
 * the one state on the billing page that turns the account read-only if it is
 * left alone — it has to look different from an invoice merely waiting to be
 * paid.
 */
const INVOICE: Record<string, Entry> = {
  draft: ['neutral', 'Draft'],
  open: ['warn', 'Unpaid'],
  paid: ['good', 'Paid'],
  failed: ['bad', 'Payment failed'],
  void: ['neutral', 'Void'],
};

/**
 * Anything with a date that runs out: a document's expiry, and from migration
 * 011 a vehicle's registration. Deliberately the same vocabulary and the same
 * colours for both — "expiring soon" has to mean one thing across the product,
 * or a person learns to read two different amber chips two different ways.
 *
 * `none` renders nothing at all rather than a grey "no expiry" chip. Most rows
 * have no date, and a column of chips saying so is noise that buries the two
 * that matter.
 */
const EXPIRY: Record<string, Entry> = {
  ok: ['good', 'In date'],
  soon: ['warn', 'Expiring soon'],
  expired: ['bad', 'Expired'],
};

/**
 * Timesheet weeks (round 44). Submitted is a fact (blue: it is with the
 * office), approved is done (green), sent back is careful (amber: it is the
 * worker's to fix), missing is stopped (red: nothing came in), a draft is
 * grey.
 */
const TIMESHEET: Record<string, Entry> = {
  missing: ['bad', 'Missing'],
  draft: ['neutral', 'Draft'],
  submitted: ['info', 'Submitted'],
  returned: ['warn', 'Sent back'],
  approved: ['good', 'Approved'],
};

/**
 * A job's variations (round 45, ZT's Variations tab): pending is careful
 * (amber, waiting on the client), approved is done (green), rejected is
 * stopped (red).
 */
const VARIATION: Record<string, Entry> = {
  pending: ['warn', 'Pending'],
  approved: ['good', 'Approved'],
  rejected: ['bad', 'Rejected'],
};

const SETS = {
  job: JOB,
  variation: VARIATION,
  timesheet: TIMESHEET,
  quote: QUOTE,
  resource: RESOURCE,
  today: TODAY,
  user: USER,
  invoice: INVOICE,
  expiry: EXPIRY,
} as const;

export function StatusBadge({
  status,
  kind = 'job',
  label: override,
}: {
  status: string;
  kind?: keyof typeof SETS;
  /**
   * Replaces the set's own wording, keeping its colour. For summary chips that
   * carry a count — "3 regos expired" — where the tone is the same statement
   * but the text is about a group rather than a row.
   */
  label?: string;
}) {
  if (kind === 'expiry' && status === 'none') return null;

  const [tone, setLabel] = SETS[kind][status] ?? (['neutral', status] as Entry);
  const label = override ?? setLabel;
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${TONE[tone]}`}
    >
      {label}
    </span>
  );
}
